<template>
  <div class="vessel-track-map" :class="{ 'is-sea': baseMode === 'sea' }">
    <div ref="mapEl" class="vessel-track-map__canvas"></div>
    <div v-if="loading" class="vessel-track-map__mask">地图加载中…</div>
    <div v-if="loadError" class="vessel-track-map__mask">{{ loadError }}</div>
  </div>
</template>

<script>
import {
  alignLatLng,
  buildStraightRoute,
  shiftPathToFrame,
  unwrapLatLngs
} from '@/utils/seaRoute'
import {
  freightowerMapUrl,
  freightowerSatUrl,
  shipdtMapUrl,
  shipdtSatUrl,
  openSeaBaseUrl,
  openSeaMarkUrl,
  chartTileBase,
  registerChartLayer,
  registerNoReferrerTileLayer
} from '@/utils/vesselMapTiles'

let leafletLoader = null

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L)
  if (leafletLoader) return leafletLoader
  leafletLoader = new Promise((resolve, reject) => {
    const cssId = 'leaflet-css-cdn'
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link')
      link.id = cssId
      link.rel = 'stylesheet'
      link.href = 'https://cdn.bootcdn.net/ajax/libs/leaflet/1.9.4/leaflet.css'
      document.head.appendChild(link)
    }
    const script = document.createElement('script')
    script.src = 'https://cdn.bootcdn.net/ajax/libs/leaflet/1.9.4/leaflet.js'
    script.onload = () => resolve(window.L)
    script.onerror = () => reject(new Error('地图脚本加载失败'))
    document.head.appendChild(script)
  })
  return leafletLoader
}

/** 俯视船位箭头（AIS 常见三角船标） */
const SHIP_ICON_HTML = `
  <div class="vt-ship-icon" title="船舶当前位置">
    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" width="40" height="40">
      <defs>
        <filter id="vtShipShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#000" flood-opacity="0.35"/>
        </filter>
      </defs>
      <g filter="url(#vtShipShadow)">
        <path d="M24 4 L40 40 L24 32 L8 40 Z" fill="#0d5bd6" stroke="#ffffff" stroke-width="2.2" stroke-linejoin="round"/>
        <path d="M24 12 L32 34 L24 29 L16 34 Z" fill="#5aa2ff" opacity="0.95"/>
      </g>
    </svg>
  </div>
`

const CIRCLED_NUMS = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳'

function circledNum(n) {
  if (n >= 1 && n <= 20) return CIRCLED_NUMS[n - 1]
  return String(n)
}

function escapeHtml(text) {
  return String(text == null ? '' : text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  name: 'VesselTrackMap',
  props: {
    mapLat: { type: Number, default: null },
    mapLon: { type: Number, default: null },
    trackPoints: { type: Array, default: () => [] },
    planBerths: { type: Array, default: () => [] },
    routeLegs: { type: Array, default: () => [] },
    vesselName: { type: String, default: '' }
  },
      data() {
    return {
      loading: true,
      loadError: '',
      map: null,
      layerGroup: null,
      shipIcon: null,
      baseMode: 'ft',
      _layersControl: null,
      _layerFt: null,
      _layerShipdt: null,
      _layerSat: null,
      _layerSea: null,
      _mapTileFails: 0,
      _fallbackStage: 0 // 0=飞驼 1=船达通 2=卫星
    }
  },
  watch: {
    trackPoints: { deep: true, handler() { this.$nextTick(() => this.renderMap()) } },
    planBerths: { deep: true, handler() { this.$nextTick(() => this.renderMap()) } },
    routeLegs: { deep: true, handler() { this.$nextTick(() => this.renderMap()) } },
    mapLat() { this.$nextTick(() => this.renderMap()) },
    mapLon() { this.$nextTick(() => this.renderMap()) }
  },
  mounted() {
    this.initMap()
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    async initMap() {
      this.loading = true
      this.loadError = ''
      try {
        const L = await loadLeaflet()
        if (!this.$refs.mapEl) return
        registerChartLayer(L)
        registerNoReferrerTileLayer(L)

        this.map = L.map(this.$refs.mapEl, {
          zoomControl: true,
          attributionControl: false
        })

        const emptyGif = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
        const tileOpts = { minZoom: 2, maxZoom: 18, errorTileUrl: emptyGif }
        // 必须 no-referrer：船达通对测试域 Referer 会 403，开发 localhost 却正常
        const layerFt = L.tileLayer.noReferrer(freightowerMapUrl(), tileOpts)
        const layerShipdt = L.tileLayer.noReferrer(shipdtMapUrl(), tileOpts)
        const layerSatFt = L.tileLayer.noReferrer(freightowerSatUrl(), tileOpts)
        const layerSatShipdt = L.tileLayer.noReferrer(shipdtSatUrl(), tileOpts)
        const layerSat = L.layerGroup([layerSatFt])
        // 飞驼卫星多次失败时，换上船达通卫星（仍显示为「卫星」）
        layerSatFt.on('tileerror', () => {
          if (this.baseMode !== 'sat') return
          this._satFails = (this._satFails || 0) + 1
          if (this._satFails < 6) return
          if (layerSat.hasLayer(layerSatFt)) layerSat.removeLayer(layerSatFt)
          if (!layerSat.hasLayer(layerSatShipdt)) layerSat.addLayer(layerSatShipdt)
        })

        const layerOsm = L.tileLayer.noReferrer(openSeaBaseUrl(), { minZoom: 2, maxZoom: 18, opacity: 1 })
        const layerMark = L.tileLayer.noReferrer(openSeaMarkUrl(), { minZoom: 2, maxZoom: 18, opacity: 0.95 })
        const layerChart = L.tileLayer.chartLayer(chartTileBase(), {
          minZoom: 2,
          maxZoom: 18,
          opacity: 0.85,
          errorTileUrl: emptyGif
        })
        const layerSea = L.layerGroup([layerOsm, layerMark, layerChart])

        this._layerFt = layerFt
        this._layerShipdt = layerShipdt
        this._layerSat = layerSat
        this._layerSea = layerSea

        layerFt.addTo(this.map)
        this.baseMode = 'ft'
        this._mapTileFails = 0
        this._fallbackStage = 0
        layerFt.on('tileerror', () => this.fallbackBasemapIfNeeded('ft'))
        layerShipdt.on('tileerror', () => this.fallbackBasemapIfNeeded('shipdt'))

        const baseMaps = {
          地图1: layerFt,
          地图2: layerShipdt,
          卫星: layerSat,
          海图: layerSea
        }
        this._layersControl = L.control.layers(baseMaps, null, { position: 'bottomright' })
        this._layersControl.addTo(this.map)
        this.map.on('baselayerchange', (e) => {
          const name = (e && e.name) || ''
          if (name.indexOf('海图') >= 0) this.baseMode = 'sea'
          else if (name.indexOf('卫星') >= 0) this.baseMode = 'sat'
          else if (name.indexOf('地图2') >= 0) this.baseMode = 'shipdt'
          else this.baseMode = 'ft'
          this._mapTileFails = 0
          if (this.baseMode === 'ft') this._fallbackStage = 0
          else if (this.baseMode === 'shipdt') this._fallbackStage = 1
          else if (this.baseMode === 'sat') this._fallbackStage = 2
        })

        this.layerGroup = L.layerGroup().addTo(this.map)
        this.shipIcon = L.divIcon({
          className: 'vt-ship-icon-wrap',
          html: SHIP_ICON_HTML,
          iconSize: [40, 40],
          iconAnchor: [20, 20],
          popupAnchor: [0, -18]
        })
        this.loading = false
        this.renderMap()
        this.$nextTick(() => {
          if (this.map) this.map.invalidateSize()
        })
      } catch (e) {
        this.loading = false
        this.loadError = (e && e.message) || '地图加载失败'
      }
    },
    /**
     * 飞驼瓦片失败 → 船达通；船达通再失败 → 卫星。
     * 不自动切海图（海图仅手动）。
     */
    fallbackBasemapIfNeeded(from) {
      if (this.baseMode === 'sea' || this.baseMode === 'sat') return
      if (from === 'ft' && this.baseMode !== 'ft') return
      if (from === 'shipdt' && this.baseMode !== 'shipdt') return
      this._mapTileFails += 1
      // 视口内会并发多格；阈值略高，避免偶发误差就跳到卫星
      if (this._mapTileFails < 16) return

      const map = this.map
      if (!map) return
      let nextName = ''
      let nextLayer = null
      if (from === 'ft' && this._fallbackStage === 0) {
        nextName = '地图2'
        nextLayer = this._layerShipdt
        this._fallbackStage = 1
        this.baseMode = 'shipdt'
      } else if (from === 'shipdt' && this._fallbackStage <= 1) {
        nextName = '卫星'
        nextLayer = this._layerSat
        this._fallbackStage = 2
        this.baseMode = 'sat'
      } else {
        return
      }
      this._mapTileFails = 0
      const prev = from === 'ft' ? this._layerFt : this._layerShipdt
      if (prev && map.hasLayer(prev)) map.removeLayer(prev)
      if (nextLayer && !map.hasLayer(nextLayer)) nextLayer.addTo(map)
      this.syncBaseLayerRadio(nextName)
    },
    syncBaseLayerRadio(labelWanted) {
      const map = this.map
      if (!map || !labelWanted) return
      try {
        const inputs = map.getContainer().querySelectorAll('.leaflet-control-layers-base input[type="radio"]')
        inputs.forEach((input) => {
          const label = input.parentElement && input.parentElement.textContent
            ? input.parentElement.textContent.trim()
            : ''
          if (label.indexOf(labelWanted) >= 0) {
            input.checked = true
            input.dispatchEvent(new Event('click', { bubbles: true }))
          }
        })
      } catch (e) { /* ignore */ }
    },
    /** @deprecated 保留旧名避免外部调用报错 */
    fallbackToSeaIfNeeded() {
      this.fallbackBasemapIfNeeded(this.baseMode === 'shipdt' ? 'shipdt' : 'ft')
    },
    portLatLng(p) {
      const lat = Number(p && p.lat)
      const lon = Number(p && p.lon)
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
      return [lat, lon]
    },
    pointsFromLeg(leg) {
      const raw = (leg && leg.points) || []
      return raw.map(p => {
        const lat = Number(p.lat)
        const lon = Number(p.lon)
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
        return [lat, lon]
      }).filter(Boolean)
    },
    /** 折线经度展开到同一世界副本，避免美线跨日界线横穿亚欧 */
    drawPolyline(L, pts, style) {
      if (!pts || pts.length < 2 || !this.layerGroup) return
      let path = unwrapLatLngs(pts)
      if (Number.isFinite(this._frameLon)) {
        path = shiftPathToFrame(path, this._frameLon)
      }
      if (path.length < 2) return
      this._frameLon = path[path.length - 1][1]
      L.polyline(path, style).addTo(this.layerGroup)
      path.forEach(ll => this._bounds.push(ll))
    },
    drawStraightLeg(L, a, b, style) {
      this.drawPolyline(L, buildStraightRoute(a, b), style)
    },
    /** 港口/船位对齐到航线展开帧 */
    frameLatLng(ll) {
      return alignLatLng(ll, this._frameLon)
    },
    renderMap() {
      const L = window.L
      if (!L || !this.map || !this.layerGroup) return
      this.layerGroup.clearLayers()
      this._bounds = []
      this._frameLon = null

      const trackLatLngs = (this.trackPoints || [])
        .map(p => {
          const lat = Number(p.lat)
          const lon = Number(p.lon)
          if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
          return [lat, lon]
        })
        .filter(Boolean)

      if (trackLatLngs.length > 1) {
        this.drawPolyline(L, trackLatLngs, { color: '#1b6cff', weight: 3, opacity: 0.92 })
      }

      const legs = Array.isArray(this.routeLegs) ? this.routeLegs : []
      if (legs.length) {
        legs.forEach(leg => {
          const pts = this.pointsFromLeg(leg)
          if (pts.length < 2) return
          const dashed = !!leg.dashed
          this.drawPolyline(L, pts, {
            color: dashed ? '#a8abb2' : '#1b6cff',
            weight: dashed ? 2.5 : 3,
            opacity: dashed ? 0.9 : 0.85,
            dashArray: dashed ? '8 8' : null
          })
        })
      } else {
        const ports = (this.planBerths || []).map(p => ({
          ...p,
          ll: this.portLatLng(p)
        })).filter(p => p.ll && !p.isShipPosition)

        let lastPassedIdx = -1
        let nextIdx = -1
        for (let i = 0; i < ports.length; i++) {
          if (ports[i].passed) lastPassedIdx = i
        }
        for (let i = 0; i < ports.length; i++) {
          if (!ports[i].passed && nextIdx < 0) {
            nextIdx = i
            break
          }
        }
        if (nextIdx < 0 && ports.length) nextIdx = ports.length - 1

        const shipLat = Number(this.mapLat)
        const shipLon = Number(this.mapLon)
        const hasShip = Number.isFinite(shipLat) && Number.isFinite(shipLon)
        const shipLl = hasShip ? [shipLat, shipLon] : null

        for (let i = 0; i < ports.length - 1; i++) {
          if (!(ports[i].passed && ports[i + 1].passed)) continue
          this.drawStraightLeg(L, ports[i].ll, ports[i + 1].ll, {
            color: '#1b6cff', weight: 3, opacity: 0.8
          })
        }
        if (hasShip && lastPassedIdx >= 0 && nextIdx > lastPassedIdx) {
          this.drawStraightLeg(L, ports[lastPassedIdx].ll, shipLl, {
            color: '#1b6cff', weight: 3, opacity: 0.9
          })
          this.drawStraightLeg(L, shipLl, ports[nextIdx].ll, {
            color: '#a8abb2', weight: 2.5, opacity: 0.9, dashArray: '8 8'
          })
          for (let i = nextIdx; i < ports.length - 1; i++) {
            this.drawStraightLeg(L, ports[i].ll, ports[i + 1].ll, {
              color: '#a8abb2', weight: 2, opacity: 0.85, dashArray: '8 8'
            })
          }
        } else {
          for (let i = 0; i < ports.length - 1; i++) {
            const passedLeg = !!ports[i].passed
            this.drawStraightLeg(L, ports[i].ll, ports[i + 1].ll, {
              color: passedLeg ? '#1b6cff' : '#a8abb2',
              weight: passedLeg ? 3 : 2,
              opacity: passedLeg ? 0.8 : 0.85,
              dashArray: passedLeg ? null : '8 8'
            })
          }
        }
      }

      // 若尚无折线建立帧（仅有点），用首港/船位初始化，再统一对齐
      if (!Number.isFinite(this._frameLon)) {
        const firstPort = (this.planBerths || []).map(p => this.portLatLng(p)).find(Boolean)
        const shipLat0 = Number(this.mapLat)
        const shipLon0 = Number(this.mapLon)
        if (firstPort) this._frameLon = firstPort[1]
        else if (Number.isFinite(shipLat0) && Number.isFinite(shipLon0)) this._frameLon = shipLon0
      }

      const ports = (this.planBerths || []).map(p => ({
        ...p,
        ll: this.frameLatLng(this.portLatLng(p))
      })).filter(p => p.ll && !p.isShipPosition)

      let futureSeq = 0
      ports.forEach(p => {
        const isPassed = !!p.passed
        const isCurrent = !!p.current && !p.isShipPosition
        // 计划节点（含当前目的港）用 ①②③ + 港名；已过港仍用蓝点
        const isPlanNode = !isPassed
        const name = p.portCn || p.port || p.portCode || '港口'
        const time = p.nodeTimeShort || p.nodeTime || p.etd || p.eta || ''
        const tag = p.nodeTag || ''
        const tip = `${name} ${p.portCode || ''}<br/>${tag} ${time}`.trim()

        if (isPlanNode) {
          futureSeq += 1
          const num = circledNum(futureSeq)
          const safeName = escapeHtml(name)
          const icon = L.divIcon({
            className: 'vt-future-port-wrap' + (isCurrent ? ' is-current' : ''),
            html: `<div class="vt-future-port${isCurrent ? ' is-current' : ''}" title="${safeName}">
              <span class="vt-future-port__num">${num}</span>
              <span class="vt-future-port__name">${safeName}</span>
            </div>`,
            iconSize: [96, 36],
            iconAnchor: [14, 18]
          })
          const marker = L.marker(p.ll, { icon, zIndexOffset: 400 + futureSeq })
          marker.bindTooltip(tip, { permanent: false, direction: 'top', sticky: true })
          marker.addTo(this.layerGroup)
        } else {
          const marker = L.circleMarker(p.ll, {
            radius: 6,
            color: '#1b6cff',
            fillColor: '#66b1ff',
            fillOpacity: 0.95,
            weight: 2
          })
          marker.bindTooltip(tip, { permanent: false, direction: 'top', sticky: true })
          marker.addTo(this.layerGroup)
        }
        this._bounds.push(p.ll)
      })

      const shipLat = Number(this.mapLat)
      const shipLon = Number(this.mapLon)
      const hasShip = Number.isFinite(shipLat) && Number.isFinite(shipLon)
      if (hasShip) {
        const shipLl = this.frameLatLng([shipLat, shipLon])
        const shipMarker = this.shipIcon
          ? L.marker(shipLl, { icon: this.shipIcon, zIndexOffset: 800 })
          : L.circleMarker(shipLl, {
            radius: 9,
            color: '#f56c6c',
            fillColor: '#1b6cff',
            fillOpacity: 1,
            weight: 2
          })
        shipMarker.bindTooltip(this.vesselName || '当前位置', { permanent: false, direction: 'top' })
        shipMarker.addTo(this.layerGroup)
        this._bounds.push(shipLl)
      }

      const bounds = this._bounds
      if (bounds.length > 1) {
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 })
      } else if (bounds.length === 1) {
        this.map.setView(bounds[0], 6)
      } else {
        this.map.setView([20, 110], 3)
      }
      this.$nextTick(() => {
        if (this.map) this.map.invalidateSize()
      })
    }
  }
}
</script>

<style scoped>
.vessel-track-map {
  position: relative;
  width: 100%;
  height: 420px;
  background: #f5f7fa;
}
.vessel-track-map__canvas {
  width: 100%;
  height: 100%;
}
.vessel-track-map__mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 247, 250, 0.82);
  color: #909399;
  font-size: 13px;
  z-index: 2;
}
</style>

<style>
.vt-ship-icon-wrap {
  background: transparent !important;
  border: none !important;
}
.vt-ship-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vt-ship-icon svg {
  display: block;
}
.vt-future-port-wrap {
  background: transparent !important;
  border: none !important;
}
.vt-future-port {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  pointer-events: auto;
}
.vt-future-port__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  color: #606266;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  border: 2px solid #909399;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
}
.vt-future-port.is-current .vt-future-port__num {
  color: #fff;
  background: #1b6cff;
  border-color: #0b4fd6;
}
.vt-future-port__name {
  max-width: 88px;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.92);
  color: #303133;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  border: 1px solid rgba(144, 147, 153, 0.55);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}
.vessel-track-map.is-sea .vt-future-port__name {
  background: rgba(255, 255, 255, 0.94);
}
</style>
